import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'

import { DialogBase } from 'components/dialog'

import {
	PredictionCondition, PredictionSource
} from 'interface/carenet/prediction'

import { MrpPredictionForm, useMrpPredictionForm } from '../form/mrp'
import { LvPredictionForm, useLvPredictionForm } from '../form/lv'
import { usePrediction } from '../hooks'
import { History as HistoryIcon } from "@mui/icons-material"
import { useListProduct } from 'interface/carenet/product'
import { flatten } from 'shared/util'

type TabType = 'none' | PredictionSource

type PredictionDialogProps = {
	manufacturerId?: number
	predictionCondition: PredictionCondition | null
	predictionState: ReturnType<typeof usePrediction>
	onClickHistory: () => void
  onClose: () => void
}
export const PredictionDialog = ({
	manufacturerId,
	predictionCondition,
	predictionState: {
		posting: predictionPosting,
		errors: predictionErrors,
		setPredictionCondition,
		predictionHistory
	},
	onClickHistory,
  onClose
}: PredictionDialogProps) => {
	const productListState = useListProduct(manufacturerId)
	const [tab, setTab] = useState<TabType>(predictionCondition?.source ?? 'none')

	const mrpForm = useMrpPredictionForm({
		productList: productListState.data?.list ?? [],
		condition: predictionCondition
	})
	const onMrpSubmit = useMemo(
		() => mrpForm.form.handleSubmit(input => {
			setPredictionCondition(mrpForm.submit(input))
			onClose()
		}),
		[mrpForm, setPredictionCondition, onClose]
	)

	const lvForm = useLvPredictionForm({
		productList: productListState.data?.list ?? [],
		condition: predictionCondition
	})
	const onLvSubmit = useMemo(
		() => lvForm.form.handleSubmit(input => {
			setPredictionCondition(lvForm.submit(input))
			onClose()
		}),
		[lvForm, setPredictionCondition, onClose]
	)

	useEffect(
		() => {
			if (predictionCondition?.source === 'lv') setTab('lv')
			else if (predictionCondition?.source === 'mrp') setTab('mrp')
			else setTab('none')
		},
		[predictionCondition]
	)
	const { posting, errors } = useMemo(
		() => ({
			posting: predictionPosting || productListState.loading,
			errors: predictionErrors.concat(flatten([productListState.error]))
		}),
		[predictionPosting, predictionErrors, productListState]
	)
	const onOk = useCallback(
		async () => {
			if (posting) return
			if (tab === 'lv') {
				onLvSubmit()
			} else if (tab === 'mrp') {
				onMrpSubmit()
			} else {
				setPredictionCondition(null)
				onClose()
			}
		},
		[posting, tab, onLvSubmit, onMrpSubmit, setPredictionCondition, onClose]
	)
	return (
    <DialogBase
      open
			title="視聴予測"
      fullWidth
      maxWidth="sm"
      error={errors}
			isLoading={posting}
      body={
				<Stack direction='column' spacing={2} flexGrow={1}>
					<RadioGroup
						value={tab}
						onChange={(e) => setTab(e.target.value as TabType)}
					>
						<Stack direction='row' spacing={0}>
							<FormControlLabel value="none" control={<Radio />} label='無効' />
							<FormControlLabel value="mrp" control={<Radio />} label='MRPlus'/>
							<FormControlLabel value="lv" control={<Radio />} label='Web講演会'/>
						</Stack>
					</RadioGroup>

					<Box width="100%" flexGrow={1} display={tab === 'none' ? undefined : 'none'}>
						<Alert severity="info">
							視聴予測を実行するサービスを選択してください
						</Alert>
					</Box>
					<MrpPredictionForm {...mrpForm} flexGrow={1} display={tab === 'mrp' ? undefined : 'none'}/>
					<LvPredictionForm {...lvForm} flexGrow={1} display={tab === 'lv' ? undefined : 'none'}/>
				</Stack>
			}
			action={{
				left: (
					<Button
						variant="contained"
						size="large"
						startIcon={<HistoryIcon/>}
						onClick={onClickHistory}
						disabled={predictionHistory.length === 0}
					>
						予測履歴
					</Button>
				),
				onOk,
				onCancel: onClose
			}}
      onClose={onClose}
    />
  )
}
